// Desktop shell: persistent sidebar nav, one content column capped at 1120px.
// Overlays are scoped to <main> so they never cross the sidebar.

import React, { useMemo, useState } from "react";
import { Lightbulb } from "lucide-react";
import { Button, IconButton, SlideOver, Toast, useToast } from "./ui.jsx";
import { formatLoginTime } from "./session.js";
import ResidentDashboard from "./ResidentDashboard.jsx";
import {
  ManagerContacts, ResidentIntake, RoleToggle, TicketDetail, TicketList,
  RESIDENT_SUGGESTIONS,
} from "./residentParts.jsx";
import {
  ChatThread, Composer, DocumentBrowser, DraftPane, DraftSheet, OnboardingWizard,
  ResolutionDetail, ResolutionsDashboard, SettingsPanel, SuggestionList,
  SUGGESTIONS,
} from "./parts.jsx";
import { navItemsWithAccess, navIcons } from "./navItems.js";

const HEADER_OFFSET = 96;

export default function DesktopApp({ bq, riq, session, role = "board", setRole, allowRoleSwitch = true, theme, font, setTheme, setFont, debugOn, toggleDebug, onSignOut }) {
  const resident = role === "resident";
  // session.communityName is set at login and never blank while signed in;
  // bq.community is the backend's own record, which lags one round-trip
  // behind it on first mount. Prefer the session so the header never
  // flashes "No community" for a signed-in user.
  const communityName = (session && session.communityName) || bq.community?.name || "No community";
  const [tab, setTab] = useState(resident ? "dashboard" : "counsel");
  const [ticketsOpen, setTicketsOpen] = useState(false);
  const [suggest, setSuggest] = useState(false);
  const [onboarding, setOnboarding] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [input, setInput] = useState("");
  const [resSub, setResSub] = useState("dash");
  const [selectedId, setSelectedId] = useState(null);
  const [lawMode, setLawMode] = useState("browse");
  const [activeDocId, setActiveDocId] = useState(null);
  const [filter, setFilter] = useState("");
  const [boardMember, setBoardMember] = useState("");
  const [busy, setBusy] = useState(false);
  const { toast, flash } = useToast();

  const tabs = navItemsWithAccess(role);
  const icons = navIcons();
  const title = {
    dashboard: "Dashboard", counsel: "Counsel", residentiq: "ResidentIQ", request: "Request",
    assoclaws: "Laws", resolutions: "Resolutions", settings: "Settings",
  }[tab];

  const changeRole = (next) => {
    setRole(next);
    setTab(next === "resident" ? "dashboard" : "counsel");
    setSelectedId(null);
    setTicketsOpen(false);
  };
  const selected = bq.resolutions.find((r) => r.id === selectedId) || null;

  const sections = useMemo(() => {
    const doc = bq.documents.find((d) => d.id === activeDocId) || bq.documents[0];
    const all = (doc && doc.chunks) || [];
    const q = filter.trim().toLowerCase();
    return (q ? all.filter((c) => ((c.title || "") + " " + (c.text || "")).toLowerCase().includes(q)) : all)
      .map((c, i) => ({ id: c.id || "s" + i, number: c.article || c.number || String(i + 1), title: c.title || "Section " + (i + 1), text: c.text || "" }));
  }, [bq.documents, activeDocId, filter]);

  const send = () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    if (resident) riq.ask(text);
    else if (tab === "resolutions" && resSub === "draft") bq.sendDraftMessage(text);
    else bq.ask(text, { scope: tab === "assoclaws" ? "ccr" : "all" });
  };

  const approve = async (id, name) => {
    setBusy(true);
    try { await bq.approve(id, name); flash("Signed. This resolution is now final."); }
    finally { setBusy(false); }
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      const rec = await bq.saveDraft({ title: bq.draft.title, text: bq.draft.text, createdBy: boardMember });
      bq.resetDraft();
      setSheet(false);
      setResSub("dash");
      flash("Saved as " + (rec.resolution_number || "draft") + ".");
    } catch (err) { flash(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{
      height: "100vh", position: "relative", zIndex: 0, isolation: "isolate",
      display: "flex", alignItems: "stretch", background: "var(--page)", color: "var(--ink)",
    }}>
      {/* sidebar */}
      <aside style={{ flex: "none", width: 272, borderRight: "1px solid var(--ink)", background: "var(--bg)", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "26px 22px 20px", borderBottom: "1px solid var(--ink)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <div style={{ width: 18, height: 18, background: "var(--accent)", flex: "none" }} />
            <span style={{ font: "800 19px/1 var(--bq-font-display)", letterSpacing: "-0.02em" }}>BoardIQ</span>
          </div>
          {allowRoleSwitch && (
            <RoleToggle role={role} onRole={changeRole} disableBoard={!!(session?.resident && !session.resident.board)} />
          )}
        </div>
        <nav style={{ display: "flex", flexDirection: "column" }}>
          {tabs.map((t) => {
            const Icon = icons[t.id];
            const on = tab === t.id;
            return (
              <button key={t.id} onClick={() => t.enabled && setTab(t.id)}
                disabled={!t.enabled}
                title={t.enabled ? undefined : "Not available in " + (resident ? "Resident" : "Board") + " view"}
                style={{
                  display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
                  border: "none", borderBottom: "1px solid var(--rule)",
                  background: on ? "var(--ink)" : "transparent", color: on ? "var(--bg)" : "var(--muted2)",
                  padding: "15px 22px", minHeight: 54,
                  cursor: t.enabled ? "pointer" : "not-allowed",
                  opacity: t.enabled ? 1 : 0.4,
                }}>
                <Icon size={20} strokeWidth={2} color={on ? "var(--accent)" : "currentColor"} />
                <span style={{ flex: 1, font: "700 13px/1 var(--bq-font-display)", letterSpacing: ".04em", textTransform: "uppercase" }}>
                  {t.label}
                </span>
                {on && <span style={{ flex: "none", width: 8, height: 8, background: "var(--accent)" }} />}
              </button>
            );
          })}
        </nav>
        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "16px 22px", borderTop: "1px solid var(--ink)", display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ font: "700 12px/1.2 var(--bq-font-display)", letterSpacing: ".06em", textTransform: "uppercase" }}>{communityName}</span>
            <span style={{ font: "400 11px/1.3 var(--bq-font-body)", color: "var(--muted)" }}>
              {(bq.totalChunks || 0) + " sections indexed · " + bq.documents.length + " documents"}
            </span>
            {session && session.userName && (
              <span style={{ font: "400 11px/1.3 var(--bq-font-body)", color: "var(--muted)" }}>
                {session.userName}{session.loginTime ? " · signed in " + formatLoginTime(session.loginTime) : ""}
              </span>
            )}
          </div>
          <div style={{ padding: "0 22px 20px", borderTop: "1px solid var(--rule)" }}>
            <Button variant="ghost" onClick={onSignOut} style={{ width: "100%", marginTop: 12 }}>Sign out</Button>
          </div>
        </div>
      </aside>

      {/* content column */}
      <main style={{ flex: 1, minWidth: 0, maxWidth: 1120, position: "relative", display: "flex", flexDirection: "column", background: "var(--bg)", borderRight: "1px solid var(--ink)" }}>
        <div style={{ flex: "none", position: "relative", zIndex: 95, padding: "26px 26px 0", background: "var(--bg)" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, paddingBottom: 12 }}>
            <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
              <h1 style={{ margin: 0, font: "800 34px/0.92 var(--bq-font-display)", letterSpacing: "-0.03em" }}>{title}</h1>
              <div style={{ font: "700 22px/1 var(--bq-font-display)", letterSpacing: "-0.02em", color: "var(--subhead)" }}>
                {communityName}
              </div>
            </div>
            {(tab === "counsel" || tab === "residentiq") && (
              <IconButton label={suggest ? "Hide suggested questions" : "Show suggested questions"} active={suggest} onClick={() => setSuggest((v) => !v)}>
                <Lightbulb size={22} strokeWidth={2} />
              </IconButton>
            )}
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {tab === "dashboard" && (
            <ResidentDashboard
              riq={riq} session={session}
              onNewRequest={() => setTab("request")}
              onBrowseTickets={() => { setTab("request"); setTicketsOpen(true); }}
            />
          )}

          {tab === "request" && (
            riq.selectedTicketId
              ? <TicketDetail riq={riq} onBack={riq.clearSelectedTicket} />
              : ticketsOpen
                ? <TicketList tickets={riq.tickets} onBack={() => setTicketsOpen(false)} onOpenNew={() => setTicketsOpen(false)} onOpenTicket={riq.selectTicket} />
                : <ResidentIntake riq={riq} managers={riq.managers} debugOn={debugOn} onBrowseTickets={() => setTicketsOpen(true)} />
          )}

          {tab === "residentiq" && (
            <>
              {riq.messages.length > 0 && (
                <div style={{ flex: "none", display: "flex", justifyContent: "flex-end", padding: "0 26px" }}>
                  <Button variant="ghost" onClick={riq.clearChat}>Clear thread</Button>
                </div>
              )}
              <ChatThread
                messages={riq.messages} pending={riq.pending} debugOn={debugOn} debugLines={riq.debugLines}
                emptyCopy="ResidentIQ reads the same CC&Rs and Davis-Stirling text the board does, and answers in plain language. Open the idea panel for what neighbours usually ask."
              />
              <Composer value={input} onChange={setInput} onSend={send} onStop={riq.stop} pending={riq.pending} placeholder="Ask about the rules where you live" />
            </>
          )}

          {tab === "counsel" && !resident && (
            <>
              {bq.messages.length > 0 && (
                <div style={{ flex: "none", display: "flex", justifyContent: "flex-end", padding: "0 26px" }}>
                  <Button variant="ghost" onClick={bq.clearChat}>Clear thread</Button>
                </div>
              )}
              <ChatThread
                messages={bq.messages} pending={bq.pending} debugOn={debugOn} debugLines={bq.debugLines}
                emptyCopy="Davis-Stirling plus your community's own CC&Rs, read together. Answers cite the sections they came from. Open the idea panel for questions residents ask most."
              />
              <Composer value={input} onChange={setInput} onSend={send} onStop={bq.stop} pending={bq.pending} placeholder="Ask about your governing documents" />
            </>
          )}

          {tab === "assoclaws" && (
            <>
              <DocumentBrowser
                documents={bq.documents} activeDocId={activeDocId} onSelectDoc={setActiveDocId}
                mode={lawMode} onMode={setLawMode} sections={sections} filter={filter} onFilter={setFilter}
                onAskSection={(s) => { setTab("counsel"); bq.ask("Explain " + s.title + " in plain language.", { scope: "ccr" }); }}
              />
              {lawMode === "ask" && (
                <Composer value={input} onChange={setInput} onSend={send} onStop={bq.stop} pending={bq.pending} placeholder="Ask this document" />
              )}
            </>
          )}

          {tab === "resolutions" && !resident && (
            <>
              {!selectedId && (
                <div style={{ flex: "none", display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 10, padding: "0 26px", borderBottom: "1px solid var(--ink)" }}>
                  <div style={{ display: "flex", flex: "none" }}>
                    {[["dash", "Dashboard"], ["draft", "Draft"]].map(([id, label]) => {
                      const on = resSub === id;
                      return (
                        <button key={id} onClick={() => setResSub(id)}
                          style={{
                            border: "none", background: "transparent", cursor: on ? "default" : "pointer",
                            borderBottom: on ? "2px solid var(--accent)" : "none", marginBottom: on ? -1 : 0,
                            padding: "10px 16px 8px 0", font: (on ? "700 " : "500 ") + "13px/1 var(--bq-font-display)",
                            color: on ? "var(--ink)" : "var(--muted)",
                          }}>{label}</button>
                      );
                    })}
                  </div>
                  {resSub === "dash" && (
                    <div style={{ display: "flex", gap: 6, flex: "none", paddingBottom: 8 }}>
                      {["all", "draft", "approved"].map((f) => {
                        const on = bq.resFilter === f;
                        return (
                          <button key={f} onClick={() => { bq.setResFilter(f); bq.refreshResolutions(f); }}
                            style={{
                              border: on ? "1px solid var(--ink)" : "1px solid var(--rule2)",
                              background: on ? "var(--ink)" : "transparent",
                              color: on ? "var(--bg)" : "var(--muted2)",
                              padding: "6px 9px", minHeight: 30, cursor: on ? "default" : "pointer",
                              font: "600 9.5px/1 var(--bq-font-display)", letterSpacing: ".08em", textTransform: "uppercase",
                            }}>{f}</button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {selectedId ? (
                <ResolutionDetail
                  resolution={selected} busy={busy}
                  onBack={() => setSelectedId(null)}
                  onApprove={approve}
                  onDelete={async (id) => { await bq.removeDraft(id); setSelectedId(null); flash("Draft deleted."); }}
                />
              ) : resSub === "dash" ? (
                <ResolutionsDashboard
                  resolutions={bq.visibleResolutions} filter={bq.resFilter}
                  onFilter={bq.setResFilter} onOpen={setSelectedId} loading={bq.loadingResolutions}
                />
              ) : (
                <>
                  <DraftPane
                    turns={bq.draftTurns} pending={bq.draftPending} draft={bq.draft}
                    onSend={send} onPickOption={(label) => bq.sendDraftMessage(label)}
                    onOpenDraft={() => setSheet(true)}
                    emptyCopy="Describe what the board needs to adopt. Counsel asks a few questions, then writes the resolution in proper WHEREAS / RESOLVED form."
                  />
                  <Composer value={input} onChange={setInput} onSend={send} onStop={() => {}} pending={bq.draftPending} placeholder="Answer, or describe the resolution" />
                </>
              )}
            </>
          )}

          {tab === "settings" && (
            <SettingsPanel
              bq={bq} role={role} theme={theme} font={font} onTheme={setTheme} onFont={setFont}
              debugOn={debugOn} onToggleDebug={toggleDebug}
              onOpenOnboarding={() => setOnboarding(true)}
              boardMember={boardMember} onBoardMember={setBoardMember}
              managerSlot={role === "board" ? <ManagerContacts managers={riq.managers} onSave={riq.saveManagers} /> : null}
            />
          )}
        </div>

        <SlideOver open={suggest} offset={HEADER_OFFSET} onDismiss={() => setSuggest(false)} zIndex={85}>
          <SuggestionList
            items={resident ? RESIDENT_SUGGESTIONS : SUGGESTIONS}
            title={resident ? "Neighbours usually ask" : "Residents usually ask"}
            onPick={(t) => {
              setSuggest(false);
              if (resident) { setTab("residentiq"); riq.ask(t); }
              else { setTab("counsel"); bq.ask(t); }
            }}
          />
        </SlideOver>

        <SlideOver open={onboarding} offset={HEADER_OFFSET} fill zIndex={88}>
          <OnboardingWizard
            bq={bq}
            onClose={() => setOnboarding(false)}
            onFinish={(name) => { setOnboarding(false); bq.loadCommunity(); flash((name || "Community") + " onboarded · corpus live"); }}
          />
        </SlideOver>

        <SlideOver open={sheet} offset={HEADER_OFFSET} fill zIndex={70}>
          <DraftSheet
            draft={bq.draft} onChange={bq.setDraft} onSave={saveDraft} saving={busy}
            onClose={() => setSheet(false)} createdBy={boardMember} onCreatedBy={setBoardMember}
          />
        </SlideOver>
      </main>

      <Toast text={toast} />
    </div>
  );
}
